<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  cardTitle: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['confirm', 'cancel']);
const { t } = useI18n();

const selectedPreset = ref('');
const customReason = ref('');
const isSubmitting = ref(false);
const submitAttempted = ref(false);
const modalContentRef = ref(null);

// Opções predefinidas de motivos (padronizadas para análise futura com LLM)
const presetReasons = computed(() => [
  { id: 'price', label: t('PIPELINE.LOST_REASON.PRESETS.PRICE') },
  { id: 'competitor', label: t('PIPELINE.LOST_REASON.PRESETS.COMPETITOR') },
  { id: 'timing', label: t('PIPELINE.LOST_REASON.PRESETS.TIMING') },
  { id: 'no_budget', label: t('PIPELINE.LOST_REASON.PRESETS.NO_BUDGET') },
  { id: 'no_response', label: t('PIPELINE.LOST_REASON.PRESETS.NO_RESPONSE') },
  { id: 'requirements', label: t('PIPELINE.LOST_REASON.PRESETS.REQUIREMENTS') },
  { id: 'other', label: t('PIPELINE.LOST_REASON.PRESETS.OTHER') },
]);

const isFormValid = computed(() => {
  if (!selectedPreset.value) return false;
  if (selectedPreset.value === 'other' && !customReason.value.trim()) {
    return false;
  }
  return true;
});

const resetForm = () => {
  selectedPreset.value = '';
  customReason.value = '';
  isSubmitting.value = false;
  submitAttempted.value = false;
};

const handleConfirm = async () => {
  if (isSubmitting.value) return;

  submitAttempted.value = true;
  if (!isFormValid.value) {
    await nextTick();
    const invalidSelector = selectedPreset.value
      ? '#lost-reason-custom'
      : 'input[name="lost-reason"]';
    modalContentRef.value?.querySelector(invalidSelector)?.focus();
    return;
  }

  isSubmitting.value = true;

  // Se for "outro", usa o texto customizado, senão usa o label do preset
  const reason =
    selectedPreset.value === 'other'
      ? customReason.value.trim()
      : presetReasons.value.find(r => r.id === selectedPreset.value)?.label ||
        '';

  emit('confirm', reason);
};

const handleCancel = () => {
  if (isSubmitting.value) return;
  emit('cancel');
  resetForm();
};

// Reset form when modal closes
watch(
  () => props.show,
  newValue => {
    if (!newValue) {
      resetForm();
    }
  }
);
</script>

<template>
  <Modal
    :show="show"
    :close-on-backdrop-click="!isSubmitting"
    :close-on-escape="!isSubmitting"
    :show-close-button="!isSubmitting"
    initial-focus-selector="input[name='lost-reason']"
    @close="handleCancel"
  >
    <section
      ref="modalContentRef"
      class="max-w-md p-4 sm:p-6"
      :aria-busy="isSubmitting"
      data-modal="lost-reason"
    >
      <!-- Header -->
      <div class="flex items-center gap-3 mb-6">
        <div
          class="flex size-12 shrink-0 items-center justify-center rounded-full bg-n-ruby-3"
          aria-hidden="true"
        >
          <fluent-icon icon="dismiss-circle" size="20" class="text-n-ruby-11" />
        </div>
        <div>
          <h2 class="pr-8 text-heading-2 text-n-slate-12">
            {{ t('PIPELINE.LOST_REASON.MODAL_TITLE') }}
          </h2>
          <p
            v-if="cardTitle"
            class="text-label-small text-n-slate-11 truncate max-w-[280px]"
          >
            {{ cardTitle }}
          </p>
        </div>
      </div>

      <!-- Subtitle -->
      <p class="text-body-main text-n-slate-11 mb-4">
        {{ t('PIPELINE.LOST_REASON.MODAL_SUBTITLE') }}
      </p>

      <!-- Preset Options -->
      <fieldset class="mb-4 space-y-2">
        <legend class="sr-only">
          {{ t('PIPELINE.LOST_REASON.MODAL_SUBTITLE') }}
        </legend>
        <label
          v-for="preset in presetReasons"
          :key="preset.id"
          class="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors"
          :class="[
            selectedPreset === preset.id
              ? 'border-n-ruby-9 bg-n-ruby-3'
              : 'border-n-weak hover:border-n-strong',
          ]"
        >
          <input
            :id="`lost-reason-${preset.id}`"
            v-model="selectedPreset"
            type="radio"
            name="lost-reason"
            :value="preset.id"
            :disabled="isSubmitting"
            class="size-5 shrink-0 border-n-weak text-n-ruby-9 focus:ring-n-ruby-9"
          />
          <span
            class="text-label font-medium"
            :class="[
              selectedPreset === preset.id
                ? 'text-n-ruby-11'
                : 'text-n-slate-12',
            ]"
          >
            {{ preset.label }}
          </span>
        </label>
      </fieldset>

      <p
        v-if="submitAttempted && !selectedPreset"
        class="mb-4 text-sm text-n-ruby-11"
        role="alert"
      >
        {{ t('PIPELINE_MODALS.REASON_REQUIRED') }}
      </p>

      <!-- Custom Reason Input (when "other" is selected) -->
      <div v-if="selectedPreset === 'other'" class="mb-4">
        <TextArea
          id="lost-reason-custom"
          v-model="customReason"
          :label="t('PIPELINE.LOST_REASON.PRESETS.OTHER')"
          :placeholder="t('PIPELINE.LOST_REASON.OTHER_PLACEHOLDER')"
          :max-length="500"
          show-character-count
          auto-height
          min-height="5rem"
          :disabled="isSubmitting"
          :message="
            submitAttempted && !customReason.trim()
              ? t('PIPELINE_MODALS.CUSTOM_REASON_REQUIRED')
              : ''
          "
          :message-type="
            submitAttempted && !customReason.trim() ? 'error' : 'info'
          "
        />
      </div>

      <!-- Actions -->
      <div
        class="flex flex-col-reverse gap-2 border-t border-n-weak pt-4 sm:flex-row sm:items-center sm:justify-end"
      >
        <Button
          variant="ghost"
          color="slate"
          size="lg"
          :disabled="isSubmitting"
          class="w-full sm:w-auto"
          @click="handleCancel"
        >
          {{ t('PIPELINE.LOST_REASON.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="ruby"
          size="lg"
          :disabled="isSubmitting"
          :is-loading="isSubmitting"
          class="w-full sm:w-auto"
          @click="handleConfirm"
        >
          <fluent-icon icon="dismiss-circle" size="16" aria-hidden="true" />
          {{ t('PIPELINE.LOST_REASON.CONFIRM') }}
        </Button>
      </div>
    </section>
  </Modal>
</template>
